import { useTranslation } from '@kgc/i18n';
import { useBreakpoint } from '@kgc/ui';
import type { ReactNode } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WizardStep {
  title: string;
  completed: boolean;
}

type WizardShellProps =
  | {
      /**
       * progress mód: mobil → progress bar sávok, desktop → egyedi stepper
       * TypeScript strict: steps + currentStep kötelező együtt
       */
      mode: 'progress';
      steps: WizardStep[];
      currentStep: number;
      /** Opcionális custom stepper desktop/tablet-re — ha üres, progress bar mindenhol */
      stepper?: ReactNode;
      footer: ReactNode;
      children: ReactNode;
    }
  | {
      /** custom mód: stepper mindig az adott ReactNode */
      mode: 'custom';
      stepper: ReactNode;
      footer: ReactNode;
      children: ReactNode;
    };

// ─── Mobile Progress Bar ──────────────────────────────────────────────────────

interface ProgressBarProps {
  steps: WizardStep[];
  currentStep: number;
}

function MobileProgressBar({ steps, currentStep }: ProgressBarProps) {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-2 kgc-card-bg border-b border-gray-200 dark:border-[#2d7d8a]">
      {/* Sávok */}
      <div className="flex gap-1 mb-1">
        {steps.map((step, idx) => (
          <div
            key={idx}
            className="flex-1 h-1 rounded-full transition-colors"
            style={{
              backgroundColor: step.completed
                ? '#22c55e' // zöld — kész
                : idx === currentStep
                  ? '#14b8a6' // teal — aktív
                  : '#e5e7eb', // szürke — még jön
            }}
          />
        ))}
      </div>
      {/* F1: i18n — common.wizardStep (ADR-071). F2: optional chain mindkét helyen */}
      <p className="text-[11px] text-gray-500 dark:text-white/50">
        {t('common.wizardStep', { current: currentStep + 1, total: steps.length })}
        {steps[currentStep]?.title ? ` — ${steps[currentStep]?.title ?? ''}` : ''}
      </p>
    </div>
  );
}

// ─── WizardShell ─────────────────────────────────────────────────────────────

/**
 * WizardShell — Újrahasználható app-shell wrapper wizard oldalakhoz
 * - h-full flex flex-col (az AppLayout <main>-jén belül tölti ki a helyet)
 * - shrink-0 stepper/header + flex-1 scroll tartalom + shrink-0 footer
 * - Nincs position: fixed a footer-en — a tartalom belül scrollozódik
 * - Mobil: progress bar sávok a dot-ok helyett
 *
 * @see tech-spec-nav-ux-overhaul.md F1-T1, F3-T8
 */
export function WizardShell(props: WizardShellProps) {
  const { footer, children } = props;
  const { isMobile } = useBreakpoint();

  // Stepper / progress bar kiválasztása
  let stepperContent: ReactNode;

  if (props.mode === 'progress') {
    if (isMobile) {
      // Mobil: progress bar sávok
      stepperContent = <MobileProgressBar steps={props.steps} currentStep={props.currentStep} />;
    } else {
      // Desktop/tablet: egyedi stepper (vagy semmi ha nincs megadva)
      stepperContent = props.stepper ?? null;
    }
  } else {
    // custom mód: mindig az adott stepper
    stepperContent = props.stepper;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Stepper / Header */}
      <div className="shrink-0">{stepperContent}</div>

      {/* Scrollozható tartalom */}
      <div className="flex-1 overflow-y-auto kgc-bg">{children}</div>

      {/* Footer — mindig látható, nem fixed */}
      <div className="shrink-0 border-t shadow-lg kgc-card-bg border-gray-200 dark:border-[#2d7d8a]">
        {footer}
      </div>
    </div>
  );
}
